import classNames from 'classnames';
import { useRouter } from 'next/router';
import { Fragment, useState } from 'react';
import { useEffect } from 'react';
import { getTrackBackground, Range } from 'react-range';
import { Translate, useTranslate } from '~/i18n';
import { formatNumber } from '~/utils/format-price';

type PriceRangeProps = {
   // eslint-disable-next-line no-unused-vars
   handleValues: (values: number[]) => void;
   labelClassName?: string;
   rangeClassName?: string;
   values?: [number, number];
};

export const PriceRangeFilter = ({
   handleValues,
   labelClassName,
   rangeClassName,
   values,
}: PriceRangeProps) => {
   const { lang, translate } = useTranslate();
   const router = useRouter();
   const [priceRange, setPriceRange] = useState<number[]>([]);

   useEffect(() => {
      /**
       * Used in reset button click only, to reset values of range picker
       */
      values && setPriceRange(values.map(p => Number(p)));
   }, [values]);

   useEffect(() => {
      const search = { ...router.query };
      const price = search.price as string;
      if (price) {
         const [min, max] = price?.split(',');
         const from = +min;
         const to = +max;

         setPriceRange([
            from >= 0 && from <= 50000 ? from : 0,
            to >= from && to <= 50000 ? to : 50000,
         ]);
      }
   }, [router]);

   return (
      <Fragment>
         <div
            className={classNames(
               'flex items-center font-semibold text-[17px] mb-3',
               labelClassName
            )}>
            <Translate id="filters:price range" />
            {/* TODO: change this to values coming in from backend */}
            <span className="font-normal text-[13px] ltr:pl-[7px] rtl:pr-[7px]">
               {'('}
               {priceRange?.[0] > 0 ? (
                  <Translate
                     parseHTML
                     id="common:price"
                     values={{
                        amount: formatNumber(priceRange?.[0]),
                     }}
                  />
               ) : (
                  translate({ id: 'common:free' })
               )}
               {priceRange?.[1] === 0 ? '' : ' - '}
               {priceRange?.[1] > 0 ? (
                  <Translate
                     parseHTML
                     id="common:price"
                     values={{
                        amount: formatNumber(priceRange?.[1]),
                     }}
                  />
               ) : priceRange[1] === 0 ? (
                  ''
               ) : (
                  <Translate
                     parseHTML
                     id="common:price"
                     values={{
                        amount: formatNumber(50000),
                     }}
                  />
               )}
               {')'}
            </span>
         </div>
         <div className={classNames('mb-[29px]', rangeClassName)}>
            <div className="flex justify-center flex-wrap w-[245px]">
               {/* TODO change 0, 50000 throughout this func component to min max coming from backend */}
               <Range
                  values={priceRange.length > 0 ? priceRange : [0, 50000]}
                  step={100}
                  min={0}
                  max={50000}
                  rtl={lang === 'sa-ar'}
                  onFinalChange={handleValues}
                  onChange={values => setPriceRange(values)}
                  renderTrack={({ props, children }) => (
                     <div
                        onMouseDown={props.onMouseDown}
                        onTouchStart={props.onTouchStart}
                        className="flex w-full h-6"
                        style={{
                           ...props.style,
                        }}>
                        <div
                           ref={props.ref}
                           className="self-center w-full h-1 rounded-sm"
                           style={{
                              background: getTrackBackground({
                                 values: priceRange.length > 0 ? priceRange : [0, 50000],
                                 colors: ['#E9ECEF', '#212529', '#E9ECEF'],
                                 min: 0,
                                 max: 50000,
                                 rtl: lang === 'sa-ar',
                              }),
                           }}>
                           {children}
                        </div>
                     </div>
                  )}
                  renderThumb={({ props }) => (
                     <div
                        className={classNames(
                           'flex justify-center items-center bg-primary-color h-6 w-6 rounded-[7px] focus:outline-none'
                        )}
                        {...props}
                        style={{
                           ...props.style,
                        }}>
                        <div className="flex space-x-[2px] rtl:space-x-reverse">
                           <div className="w-px h-2 bg-white" />
                           <div className="w-px h-2 bg-white" />
                        </div>
                     </div>
                  )}
               />
            </div>
         </div>
      </Fragment>
   );
};
